import { FC, useEffect } from 'react'
import css from 'styled-jsx/css'
import { INotiStateItem, REMOVE } from './interface.d'
import { useNotiContext } from './NotiContext'

const DangerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 9V14"
      stroke="#F57C00"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 21.41H5.94C2.47 21.41 1.02 18.93 2.7 15.9L5.82 10.28L8.76 5C10.54 1.79 13.46 1.79 15.24 5L18.18 10.29L21.3 15.91C22.98 18.94 21.52 21.42 18.06 21.42H12V21.41Z"
      stroke="#F57C00"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.9945 17H12.0035"
      stroke="#F57C00"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17 7L7 17"
      stroke="#464F5B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 7L17 17"
      stroke="#464F5B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Notification: FC<INotiStateItem> = ({
  id,
  title,
  content = '',
  removable = true,
  timeout = 10,
  type = ''
}) => {
  const { notiDispatch } = useNotiContext()

  const remove = () => {
    notiDispatch({ type: REMOVE, payload: { id } })
  }

  useEffect(() => {
    if (timeout) {
      setTimeout(() => {
        remove()
      }, timeout * 1000)
    }
  }, [])

  if (title) {
    return (
      <div className="noti animated slide-in-down">
        <style jsx global>
          {globalStyle}
        </style>
        <div className={`notification notification--titled ${type}`}>
          <span className="notification__icon" aria-hidden="true">
            <DangerIcon />
          </span>
          <div className="notification__body">
            <p className="notification__title">{title}</p>
            <p className="notification__content" dangerouslySetInnerHTML={{ __html: content }} />
          </div>
          {removable && (
            <button className="notification__close" onClick={remove} aria-label="Close">
              <CloseIcon />
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="noti animated slide-in-down">
      <style jsx global>
        {globalStyle}
      </style>
      <div className={`notification ${type}`}>
        {removable && <button className="delete" onClick={remove} />}
        <p dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    </div>
  )
}

function Notifications({ notiList }: { notiList: INotiStateItem[] }) {
  return (
    <>
      {notiList.map((noti) => {
        return <Notification {...noti} key={noti.id} />
      })}
    </>
  )
}

const globalStyle = css.global`
  .noti {
    z-index: 1002;
    min-width: 200px;
    max-width: 400px;
    position: fixed;
    top: 64px;
    right: 32px;
    font-size: 13px;

    .notification {
      border-radius: 6px;
      padding: 12px 56px 12px 16px;
      &.is-info {
        background-color: #f1f9fe !important;
        border: 2px solid #2196f3 !important;
        box-shadow: 0px 6.4px 14.4px rgba(101, 115, 129, 0.13) !important;
        border-radius: 8px;
        color: #363636 !important;
      }
      &.is-danger {
        background-color: #eb5757;
      }
      &.is-orange {
        font-size: 14px;
        font-weight: 400;
        line-height: 24px;
        color: #363636;
        padding: 12px 56px 12px 12px;
        background-color: #fff3e0;
        border: 1px solid #f57c00;
        border-radius: 8px;
      }

      .delete {
        min-width: 24px;
        min-height: 24px;
        top: 12px;
        right: 12px;
      }

      &.notification--titled {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 16px;
        border-radius: 8px;

        &.is-orange {
          background-color: #fffaf2;
          border: 1px solid #f57c00;
        }

        .notification__icon {
          flex-shrink: 0;
          width: 24px;
          height: 24px;
        }

        .notification__body {
          flex: 1 0 0;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .notification__title {
          margin: 0;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 16px;
          line-height: 24px;
          color: #1b1b1b;
        }

        .notification__content {
          margin: 0;
          font-family: 'Poppins', sans-serif;
          font-weight: 400;
          font-size: 14px;
          line-height: 24px;
          color: #1b1b1b;
        }

        .notification__close {
          flex-shrink: 0;
          width: 24px;
          height: 24px;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }

    @media screen and (max-width: 767px) {
      top: 12px;
      left: 12px;
      right: 12px;
      min-width: calc(100% - 24px);
    }
  }
`

export default Notifications
